import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { RESOURCE_MAP } from '@pusula/shared'
import { useAuthStore, useCan } from '../../stores/auth.store'
import { api } from '../../lib/api'

interface ProtectedRouteProps {
  allowedRoles?: string[]
}

// Rota → o sayfanın okuma+yazma yetki anahtarları (en uzun eşleşme önce → prefix eşleştirme).
const ROUTE_PERMS: Array<{ route: string; perms: string[] }> = []
for (const section of RESOURCE_MAP ?? []) {
  for (const page of section.pages) {
    const keys = [page.read, page.write].filter(Boolean) as string[]
    if (page.route && keys.length) ROUTE_PERMS.push({ route: page.route, perms: keys })
  }
}
ROUTE_PERMS.sort((a, b) => b.route.length - a.route.length)

function permsForPath(path: string): string[] | null {
  const match = ROUTE_PERMS.find((r) => path === r.route || path.startsWith(`${r.route}/`))
  return match?.perms ?? null
}

export function ProtectedRoute({ allowedRoles }: ProtectedRouteProps): JSX.Element {
  const { user, isInitialized, fetchMe } = useAuthStore()
  const can = useCan()
  const location = useLocation()

  // Raporlar: ACCOUNTANT matriste REPORT_VIEW'e sahip değildir; `accountantCanViewReports` ayarı açıksa girebilir
  // (AppLayout nav kuralı ve backend requireReportAccess ile aynı).
  const isReportsPath = location.pathname === '/reports' || location.pathname.startsWith('/reports/')
  const isAccountantOnReports = user?.role === 'ACCOUNTANT' && isReportsPath
  const { data: financeSettingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['finance-settings'],
    queryFn: () => api.get('/finance/settings').then(r => r.data as { data: { accountantCanViewReports: boolean } }),
    enabled: isAccountantOnReports,
  })

  useEffect(() => {
    if (!isInitialized) {
      void fetchMe()
    }
  }, [isInitialized, fetchMe])

  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Erişim TÜM roller için yetki-tabanlı: rota bir yetkiye eşleşiyorsa ona bakılır;
  // RESOURCE_MAP dışı rotalarda güvenlik için rol listesine (allowedRoles) düşülür.
  const perms = permsForPath(location.pathname)
  if (isAccountantOnReports && !can('REPORT_VIEW')) {
    if (settingsLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )
    }
    if (!financeSettingsData?.data?.accountantCanViewReports) return <Navigate to="/dashboard" replace />
  } else if (perms) {
    if (!perms.some((p) => can(p))) return <Navigate to="/dashboard" replace />
  } else if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />
  }

  return <Outlet />
}
